/** @jsxImportSource preact */
import { useRef } from "preact/hooks";
import { z } from "zod";

import * as m from "../paraglide/messages.js";

export interface PanelSize {
  width: number;
  height: number;
}

/** 静态预览的参考尺寸；店面默认尺寸随浏览器视口计算。 */
export const DEFAULT_PANEL_SIZE: PanelSize = { width: 420, height: 620 };
const MIN_PANEL_SIZE: PanelSize = { width: 340, height: 420 };
const panelSizeSchema = z.object({
  width: z.number().positive(),
  height: z.number().positive(),
});

/** 使用 CSS 视口尺寸，不依赖显示器分辨率或设备像素比。 */
export function getDefaultPanelSize(viewport: PanelSize): PanelSize {
  return {
    width: Math.round(Math.min(640, Math.max(420, viewport.width * 0.3))),
    height: Math.round(Math.min(960, viewport.height * 0.8)),
  };
}

/** 没有有效偏好时保持自动尺寸，不把当前视口的默认值存成偏好。 */
export function readPanelSize(key: string): PanelSize | undefined {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(key) ?? "null");
    return panelSizeSchema.safeParse(value).data;
  } catch {
    // 存储不可用或旧值损坏时，仍允许在当前页面调整。
    return undefined;
  }
}

/** 小视口优先放得下；不把临时缩小的视口写回买家偏好的尺寸。 */
export function constrainPanelSize(
  size: PanelSize,
  available: PanelSize,
): PanelSize {
  return {
    width: Math.min(
      available.width,
      Math.max(MIN_PANEL_SIZE.width, size.width),
    ),
    height: Math.min(
      available.height,
      Math.max(MIN_PANEL_SIZE.height, size.height),
    ),
  };
}

interface PanelResizeProps {
  alignRight: boolean;
  heightOnly?: boolean;
  onResize: (size: PanelSize | undefined) => void;
}

/** 上角调整宽高，顶部手柄仅调高度；指针捕获允许拖出面板。 */
export function PanelResize({
  alignRight,
  heightOnly = false,
  onResize,
}: PanelResizeProps) {
  const drag = useRef<
    (PanelSize & { x: number; y: number; pointerId: number }) | null
  >(null);
  const sign = alignRight ? -1 : 1;
  const help = heightOnly
    ? m.widget_resize_height_help()
    : m.widget_resize_help();
  return (
    <button
      aria-label={
        heightOnly ? m.widget_resize_height_label() : m.widget_resize_label()
      }
      aria-description={help}
      title={help}
      type="button"
      style={{
        position: "absolute",
        top: 0,
        ...(heightOnly
          ? { left: "calc(50% - 24px)" }
          : { [alignRight ? "left" : "right"]: 0 }),
        width: heightOnly ? 48 : 24,
        height: 24,
        padding: 0,
        border: 0,
        background: "transparent",
        color: "inherit",
        cursor: heightOnly
          ? "ns-resize"
          : alignRight
            ? "nwse-resize"
            : "nesw-resize",
        touchAction: "none",
        zIndex: 1,
      }}
      onPointerDown={(event) => {
        if (event.button !== 0 || !event.isPrimary) return;
        const panel = event.currentTarget.parentElement;
        if (!panel) return;
        // 布局尺寸不包含打开动画的 scale，快速开始拖拽也不会缩小一截。
        const width = panel.offsetWidth;
        const height = panel.offsetHeight;
        drag.current = {
          width,
          height,
          x: event.clientX,
          y: event.clientY,
          pointerId: event.pointerId,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
        // 鼠标拖动不留下焦点框；Tab 聚焦仍保留原生键盘提示。
        event.currentTarget.blur();
        event.preventDefault();
      }}
      onPointerMove={(event) => {
        const start = drag.current;
        if (!start || start.pointerId !== event.pointerId) return;
        onResize({
          width: heightOnly
            ? start.width
            : start.width + sign * (event.clientX - start.x),
          height: start.height - (event.clientY - start.y),
        });
      }}
      onPointerUp={(event) => {
        if (drag.current?.pointerId !== event.pointerId) return;
        drag.current = null;
        event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={() => {
        drag.current = null;
      }}
      onLostPointerCapture={() => {
        drag.current = null;
      }}
      onKeyDown={(event) => {
        const panel = event.currentTarget.parentElement;
        if (!panel) return;
        const width = panel.offsetWidth;
        const height = panel.offsetHeight;
        const step = event.shiftKey ? 40 : 10;
        let next: PanelSize | undefined;
        switch (event.key) {
          case "ArrowLeft":
            if (heightOnly) return;
            next = { width: width - sign * step, height };
            break;
          case "ArrowRight":
            if (heightOnly) return;
            next = { width: width + sign * step, height };
            break;
          case "ArrowUp":
            next = { width, height: height + step };
            break;
          case "ArrowDown":
            next = { width, height: height - step };
            break;
          case "Home":
            next = heightOnly
              ? {
                  width,
                  height: getDefaultPanelSize(
                    window.visualViewport ?? {
                      width: window.innerWidth,
                      height: window.innerHeight,
                    },
                  ).height,
                }
              : undefined;
            break;
          default:
            return;
        }
        event.preventDefault();
        onResize(next);
      }}
    >
      {heightOnly && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 3,
            left: 12,
            width: 24,
            height: 3,
            borderRadius: 2,
            background: "currentColor",
            opacity: 0.35,
          }}
        />
      )}
    </button>
  );
}
